import { useContext } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import {
  importAdvertiserBrandService,
  previewAdvertiserBrandImportService
} from '../services/advertiser-brand'
import { invalidateAdvertiserBrandQueries } from './advertiserBrandQueryKeys'
import { useAdvertiserBrandErrorMessage } from './useAdvertiserBrands'

/**
 * Import de planilha em dois passos: `preview` (dry-run, sem toast — o erro
 * estrutural é da tela) e `confirm` (grava, invalida e avisa).
 */
export const useAdvertiserBrandImport = () => {
  const { t } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)
  const queryClient = useQueryClient()
  const getErrorMessage = useAdvertiserBrandErrorMessage()

  const preview = useMutation({
    mutationFn: async (file: File) => {
      const response = await previewAdvertiserBrandImportService(file)

      return response.data
    }
  })

  const confirm = useMutation({
    mutationFn: async (file: File) => {
      const response = await importAdvertiserBrandService(file)

      return response.data
    },
    onSuccess: result => {
      invalidateAdvertiserBrandQueries(queryClient)

      // reatribuição também é vínculo gravado
      const linked = result.created + (result.moved ?? 0)

      // linha que mudou entre o dry-run e a confirmação não aparece no review
      if (result.errors.length) {
        toast.warning(
          t('rm:advertiserProducts.import.partial', {
            linked,
            failed: result.errors.length
          })
        )

        return
      }

      toast.success(
        t('rm:advertiserProducts.import.success', { count: linked })
      )
    },
    onError: error => toast.critical(getErrorMessage(error))
  })

  return { preview, confirm, getErrorMessage }
}
